import React, { useLayoutEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  InputAdornment,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import Loader from 'src/components/Loader';
import { useKeydown } from 'src/hooks/useKeydown';
import { ReactComponent as ArrowDropDownIcon } from 'src/icons/arrowDropDown.svg';
import { ReactComponent as CheckboxOffIcon } from 'src/icons/checkboxOff.svg';
import { ReactComponent as CheckboxOnIcon } from 'src/icons/checkboxOn.svg';
import { ReactComponent as TuneIcon } from 'src/icons/tune.svg';
import palette from 'src/theme/palette';
import { getIconForMetric } from 'src/utils';

import useFiltersData from '../../../hooks/useFiltersData';
import { useConnectors } from '../../../providers/connectors/hook/useConnectors';
import {
  checkboxStyles,
  CustomPopper,
  DropdownButton,
  FiltersWrapper,
  splitsInputStyles,
  SplitsInputWrapper,
  SplitsList,
  SplitsListItem,
  SplitsListItemButton,
} from '../styled';
import {
  DashboardForm,
  FiltersProps,
  FilterWithValue,
  InputName,
  OperatorEnum,
} from '../types';
import { sortArrayBySecondArray } from '../utils';

import { Badge } from './Badge';

export const Filters = ({
  toogleFilterMetrics,
  isShownMetricFilters,
  googleAdsMetricId,
  shopifyFilterValues,
  magentoFilterValues,
  connectors,
}: FiltersProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const connectorsCtx = useConnectors();

  const { control, setValue, watch } = useFormContext<DashboardForm>();
  const [ulScrollTop, setUlScrollTop] = React.useState<{
    list: HTMLUListElement;
    position: number;
  } | null>(null);

  const [searchValue, setSearchValue] = useState<string>('');

  const googleSearchConsoleConnectorId =
    connectors?.googleSearchConsoleConnectorId ||
    connectorsCtx.googleSearchConsoleConnectorId;

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    value: FilterWithValue[],
    filter: FilterWithValue,
  ) => {
    const list = e.currentTarget.closest('ul');

    const scrollPosition = list?.scrollTop;

    if (value.some((item) => item.id === filter.id)) {
      setValue(
        InputName.filters,
        value.filter((item) => item.id !== filter.id),
      );
    } else {
      // Prima regola già pronta (es. Query/Page → Contains) così si digita subito.
      const firstOperatorKey =
        filter.operators?.[0]?.key ?? OperatorEnum.Contain;
      const cleanFilter: FilterWithValue = {
        id: filter.id,
        key: filter.key,
        label: filter.label,
        type: filter.type,
        connectorType: filter.connectorType,
        operators: filter.operators,
        expectedReturnValueType: filter.expectedReturnValueType,
        shouldAvailableValuesBeRequested:
          filter.shouldAvailableValuesBeRequested,
        availableValues: filter.availableValues,
        values: [
          {
            filtersGroup: [{ operator: firstOperatorKey, values: [] }],
          },
        ],
      };

      setValue(InputName.filters, [...value, cleanFilter]);
    }

    if (list && scrollPosition)
      setUlScrollTop({ list, position: list.scrollTop });
  };

  useLayoutEffect(() => {
    if (ulScrollTop?.list) {
      ulScrollTop.list.scrollTop = ulScrollTop.position; // Восстановить позицию
      setUlScrollTop(null); // Сбросить состояние
    }
  }, [ulScrollTop]);

  const handleClickAway = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.stopPropagation();

    if (anchorEl) setAnchorEl(null);

    if (searchValue) setSearchValue('');
  };

  const selectedMetrics = watch(InputName.metrics);

  const { filters, loading } = useFiltersData({
    skipFacebookFilters: !anchorEl || !connectors?.facebookConnectorId,
    skipShopifyFilters: !anchorEl || !connectors?.shopifyConnectorId,
    skipMagentoFilters: !anchorEl || !connectors?.magentoConnectorEnabled || !connectors?.magentoConnectorId,
    skipGoogleAdsFilters: !anchorEl || !connectors?.googleAdsConnectorId,
    skipGoogleAnalyticsFilters: !anchorEl,
    skipGoogleSearchConsoleFilters: !anchorEl || !googleSearchConsoleConnectorId,
    googleAdsMetricId,
    shopifyFilterValues,
    magentoFilterValues,
    searchValue,
    selectedMetrics,
  });

  const badgeContent = watch(InputName.filters).length;

  useKeydown('Escape', handleClickAway);

  return (
    <ClickAwayListener onClickAway={() => handleClickAway()}>
      <FiltersWrapper>
        <Box onClick={(e) => setAnchorEl(e.currentTarget)} className="filters">
          {anchorEl ? (
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                '& .MuiInputBase-root': {
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                },
                '& .MuiInputBase-input': {
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
              InputProps={{
                sx: splitsInputStyles,
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge badgeContent={badgeContent} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <DropdownButton onClick={(e) => handleClickAway(e)}>
                      <ArrowDropDownIcon />
                    </DropdownButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <SplitsInputWrapper>
              <Box display="flex" gap="5px" alignItems="center">
                <TuneIcon color={palette.grey[800]} />
                <Typography fontSize="16px" lineHeight="21px" fontWeight={600}>
                  Filters
                </Typography>
                <Badge
                  sx={{ backgroundColor: palette.grey[100] }}
                  badgeContent={badgeContent}
                />
              </Box>
            </SplitsInputWrapper>
          )}
        </Box>

        {!!badgeContent && (
          <SplitsInputWrapper onClick={toogleFilterMetrics}>
            <Box display="flex" gap="5px" alignItems="center">
              <TuneIcon
                color={
                  isShownMetricFilters
                    ? palette.primary.main
                    : palette.grey[100]
                }
              />
              <Typography fontSize="16px" lineHeight="21px" fontWeight={600}>
                {isShownMetricFilters ? 'Close filters' : 'Open filters'}
              </Typography>
            </Box>
          </SplitsInputWrapper>
        )}

        <CustomPopper
          open={!!anchorEl}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal={false}
        >
          <Controller
            name={InputName.filters}
            control={control}
            render={({ field: { value } }) => (
              <SplitsList sx={{ height: loading ? '60px' : undefined }}>
                {loading ? (
                  <Loader />
                ) : (
                  sortArrayBySecondArray(filters || [], value).map((filter) => {
                    return (
                      <SplitsListItem key={filter.id}>
                        <SplitsListItemButton
                          onClick={(e) => handleClick(e, value, filter)}
                          disableRipple
                        >
                          <Checkbox
                            sx={checkboxStyles}
                            icon={<CheckboxOffIcon height={24} width={24} />}
                            checkedIcon={
                              <CheckboxOnIcon height={24} width={24} />
                            }
                            checked={value.some(
                              (item) => item.id === filter.id,
                            )}
                          />
                          <Box display="flex" gap="5px" mr="5px">
                            {getIconForMetric(filter.connectorType)}
                          </Box>

                          <Typography>{filter.label}</Typography>
                        </SplitsListItemButton>
                      </SplitsListItem>
                    );
                  })
                )}
                {!filters?.length && !loading && (
                  <ListItem sx={{ padding: 0 }}>
                    <SplitsListItemButton disableRipple>
                      <Typography>No options</Typography>
                    </SplitsListItemButton>
                  </ListItem>
                )}
              </SplitsList>
            )}
          />
        </CustomPopper>
      </FiltersWrapper>
    </ClickAwayListener>
  );
};
